import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { MapPin, Clock, DollarSign, ChevronLeft, ChevronRight, Navigation, Users, User, Filter } from 'lucide-react';
import { format, isSameDay, parseISO } from 'date-fns';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VisitsCalendar() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState('me'); // 'me', 'all', or specific user id
  const [showFilter, setShowFilter] = useState(false);
  const [planLimits, setPlanLimits] = useState(null);

  // Check if plan supports team features (max_team_members > 1)
  const hasTeamFeatures = planLimits?.max_team_members > 1;

  useEffect(() => {
    // Fetch plan limits
    const fetchLimits = async () => {
      try {
        const response = await axios.get(`${API}/jobs/limits`);
        setPlanLimits(response.data.plan_limits);
      } catch (error) {
        console.error('Failed to fetch plan limits');
      }
    };
    fetchLimits();
  }, []);

  useEffect(() => {
    fetchJobs();
    if (hasTeamFeatures) {
      fetchTeamMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMember, hasTeamFeatures]);

  const fetchJobs = async () => {
    try {
      let url = `${API}/jobs`;
      const params = new URLSearchParams();
      
      if (hasTeamFeatures) {
        if (selectedMember === 'all') {
          params.append('team_view', 'true');
          params.append('assigned_to', 'all');
        } else if (selectedMember !== 'me') {
          params.append('assigned_to', selectedMember);
          params.append('team_view', 'true');
        }
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url);
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team/members`);
      setTeamMembers(response.data.members || []);
    } catch (error) {
      console.error('Failed to fetch team members');
    }
  };

  // Get jobs with scheduled dates
  const scheduledJobs = useMemo(() => {
    return jobs.filter(job => job.scheduled_date && job.status !== 'paid');
  }, [jobs]);

  // Get dates that have jobs
  const datesWithJobs = useMemo(() => {
    const dates = {};
    scheduledJobs.forEach(job => {
      const dateKey = format(parseISO(job.scheduled_date), 'yyyy-MM-dd');
      if (!dates[dateKey]) {
        dates[dateKey] = [];
      }
      dates[dateKey].push(job);
    });
    return dates;
  }, [scheduledJobs]);

  // Get jobs for selected date
  const jobsForSelectedDate = useMemo(() => {
    const dateKey = format(selectedDate, 'yyyy-MM-dd');
    return datesWithJobs[dateKey] || [];
  }, [selectedDate, datesWithJobs]);

  const openInMaps = (job) => {
    const address = job.full_address || job.address;
    if (!address) {
      toast.error('No address for this job');
      return;
    }
    
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOS) {
      window.location.href = `maps://maps.apple.com/?q=${encodeURIComponent(address)}`;
    } else {
      window.open(`https://maps.google.com/?q=${encodeURIComponent(address)}`, '_blank');
    }
  };

  const statusColors = {
    quote: 'bg-blue-500',
    scheduled: 'bg-amber-500',
    paid: 'bg-emerald-500'
  };

  const handlePrevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const getFilterLabel = () => {
    if (selectedMember === 'me') return 'My Jobs';
    if (selectedMember === 'all') return 'All Team';
    const member = teamMembers.find(m => m.id === selectedMember);
    return member?.name || 'Team Member';
  };

  return (
    <div className="p-4 animate-fade-in" data-testid="calendar-page">
      {/* Header */}
      <div className="mb-4">
        <h1 className="font-heading text-3xl font-bold text-slate-dark">
          Visits Calendar
        </h1>
        <p className="text-slate-500">Your scheduled jobs</p>
      </div>

      {/* Team Filter (Plans with team support) */}
      {hasTeamFeatures && (
        <div className="mb-4">
          <div className="relative">
            <button
              onClick={() => setShowFilter(!showFilter)}
              className="w-full flex items-center justify-between p-3 bg-white rounded-xl border-2 border-slate-200 hover:border-[#FF5F00] transition-colors"
            >
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-slate-400" />
                <span className="font-medium text-slate-700">{getFilterLabel()}</span>
              </div>
              <ChevronLeft className={`w-5 h-5 text-slate-400 transition-transform ${showFilter ? 'rotate-90' : '-rotate-90'}`} />
            </button>
            
            {showFilter && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border-2 border-slate-200 shadow-lg z-10 overflow-hidden">
                <button
                  onClick={() => { setSelectedMember('me'); setShowFilter(false); }}
                  className={`w-full flex items-center gap-2 p-3 hover:bg-slate-50 ${selectedMember === 'me' ? 'bg-orange-50 text-[#FF5F00]' : 'text-slate-700'}`}
                >
                  <User className="w-5 h-5" />
                  <span>My Jobs</span>
                </button>
                <button
                  onClick={() => { setSelectedMember('all'); setShowFilter(false); }}
                  className={`w-full flex items-center gap-2 p-3 hover:bg-slate-50 ${selectedMember === 'all' ? 'bg-orange-50 text-[#FF5F00]' : 'text-slate-700'}`}
                >
                  <Users className="w-5 h-5" />
                  <span>All Team Jobs</span>
                </button>
                {teamMembers.filter(m => m.id !== user?.id).map(member => (
                  <button
                    key={member.id}
                    onClick={() => { setSelectedMember(member.id); setShowFilter(false); }}
                    className={`w-full flex items-center gap-2 p-3 hover:bg-slate-50 ${selectedMember === member.id ? 'bg-orange-50 text-[#FF5F00]' : 'text-slate-700'}`}
                  >
                    <User className="w-5 h-5" />
                    <span>{member.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          data-testid="prev-month"
          onClick={handlePrevMonth}
          className="w-10 h-10 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center hover:bg-slate-50"
        >
          <ChevronLeft className="w-5 h-5 text-slate-600" />
        </button>
        
        <h2 className="font-heading text-xl font-bold text-slate-dark">
          {format(currentMonth, 'MMMM yyyy')}
        </h2>
        
        <button
          data-testid="next-month"
          onClick={handleNextMonth}
          className="w-10 h-10 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center hover:bg-slate-50"
        >
          <ChevronRight className="w-5 h-5 text-slate-600" />
        </button>
      </div>

      {/* Calendar - Full Width */}
      <div className="bg-white rounded-xl border-2 border-slate-200 shadow-hard p-2 sm:p-4 mb-6">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(date) => date && setSelectedDate(date)}
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          fullWidth={true}
          modifiers={{
            hasJobs: (date) => {
              const dateKey = format(date, 'yyyy-MM-dd');
              return !!datesWithJobs[dateKey];
            }
          }}
          modifiersStyles={{
            hasJobs: {
              fontWeight: 'bold',
              backgroundColor: '#FFF7ED',
              color: '#FF5F00',
              borderRadius: '50%'
            }
          }}
          components={{
            DayContent: ({ date }) => {
              const dateKey = format(date, 'yyyy-MM-dd');
              const jobCount = datesWithJobs[dateKey]?.length || 0;
              return (
                <div className="relative w-full h-full flex items-center justify-center">
                  {date.getDate()}
                  {jobCount > 0 && (
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-[#FF5F00] rounded-full" />
                  )}
                </div>
              );
            }
          }}
        />
      </div>

      {/* Selected Date Info */}
      <div className="mb-4">
        <h3 className="font-heading text-lg font-bold text-slate-dark">
          {format(selectedDate, 'EEEE, MMMM d')}
        </h3>
        <p className="text-slate-500 text-sm">
          {jobsForSelectedDate.length} {jobsForSelectedDate.length === 1 ? 'visit' : 'visits'} scheduled
        </p>
      </div>

      {/* Jobs for Selected Date */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2].map(i => (
            <div key={i} className="h-24 bg-slate-200 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : jobsForSelectedDate.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-xl border-2 border-dashed border-slate-200">
          <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No visits scheduled for this day</p>
          <button
            onClick={() => navigate('/new-job')}
            className="mt-3 text-[#FF5F00] font-bold hover:underline"
          >
            + Schedule a job
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {jobsForSelectedDate.map((job) => {
            const address = job.full_address || job.address;
            return (
              <div
                key={job.id}
                data-testid={`calendar-job-${job.id}`}
                className="bg-white rounded-xl border-2 border-slate-200 shadow-hard overflow-hidden"
              >
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-heading text-lg font-bold text-slate-dark">
                        {job.client_name}
                      </h4>
                      <p className="text-slate-500 text-sm line-clamp-1">
                        {job.description}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-bold text-white ${statusColors[job.status]}`}>
                      {job.status}
                    </span>
                  </div>

                  {/* Assigned To (Team plans) */}
                  {hasTeamFeatures && job.assigned_to_name && (
                    <div className="flex items-center gap-2 text-slate-600 text-sm mb-2">
                      <User className="w-4 h-4" />
                      <span>Assigned to: <strong>{job.assigned_to_name}</strong></span>
                    </div>
                  )}

                  {/* Time Display */}
                  {job.scheduled_time && (
                    <div className="flex items-center gap-2 text-slate-600 text-sm mb-2">
                      <Clock className="w-4 h-4" />
                      <span>
                        {(() => {
                          const [h, m] = job.scheduled_time.split(':').map(Number);
                          const ampm = h >= 12 ? 'PM' : 'AM';
                          return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${ampm}`;
                        })()}
                        {job.duration && ` (${job.duration} min)`}
                      </span>
                    </div>
                  )}

                  {/* Recurring Badge */}
                  {job.is_recurring && (
                    <div className="inline-flex items-center gap-1 text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded-full mb-2">
                      <span>🔄</span>
                      <span>Recurring ({job.recurrence_pattern})</span>
                    </div>
                  )}

                  {/* Address with Map Link */}
                  {address && (
                    <button
                      data-testid={`open-map-${job.id}`}
                      onClick={() => openInMaps(job)}
                      className="w-full flex items-center gap-2 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group mt-2"
                    >
                      <div className="w-10 h-10 rounded-full bg-[#FF5F00] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                        <Navigation className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-slate-900 font-medium text-sm line-clamp-1">
                          {address}
                        </p>
                        <p className="text-[#FF5F00] text-xs font-bold">
                          TAP TO OPEN IN MAPS
                        </p>
                      </div>
                    </button>
                  )}

                  {/* Bottom row */}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                    <span className="font-heading text-xl font-bold text-slate-dark flex items-center">
                      <DollarSign className="w-5 h-5" />
                      {(job.quote_amount || 0).toLocaleString()}
                    </span>
                    <button
                      onClick={() => navigate(`/job/${job.id}`)}
                      className="text-slate-500 text-sm hover:text-[#FF5F00] font-medium"
                    >
                      View Details →
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upcoming Jobs Summary */}
      {scheduledJobs.length > 0 && (
        <div className="mt-6 p-4 bg-slate-dark rounded-xl">
          <h3 className="font-heading text-lg font-bold text-white mb-2">
            {selectedMember === 'all' ? 'Team' : ''} Upcoming Visits
          </h3>
          <div className="flex items-center gap-4 text-slate-300">
            <div>
              <span className="text-2xl font-heading font-bold text-white">{scheduledJobs.length}</span>
              <span className="text-sm ml-1">scheduled</span>
            </div>
            <div className="w-px h-8 bg-slate-600" />
            <div>
              <span className="text-2xl font-heading font-bold text-[#FF5F00]">
                ${scheduledJobs.reduce((sum, j) => sum + (j.quote_amount || 0), 0).toLocaleString()}
              </span>
              <span className="text-sm ml-1">total value</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
